import React, { useState, useEffect } from 'react';
import { noop } from 'lodash';
import { TreeSelect } from 'antd';
import { useTranslation } from 'react-i18next';

/**
 * CITTreeSelect component - used for GU/Alliance select.
 *
 * @param {Object} props - Props for the CITTreeSelect component.
 * @param {Array} props.options - The options for the tree select.
 * @param {Function} props.onChange - The function to be called when the tree select value changes.
 * @param {Boolean} props.clear - A flag indicating whether to clear the tree select value.
 * @returns {JSX.Element} The CITTreeSelect component.
 * @example
 *  <CITTreeSelect
        options={
            [
                {
                    name: 'allianceName or guWithoutAllianceName', 
                    gu: ['gu1', 'gu2']
                }, 
                // ...
            ]
        }
        onChange={setAllianceAndGu}
        clear={true}
    />
 */
function CITTreeSelect({ options, onChange, clear, ...props }) {
    const [value, setValue] = useState([]);

    const { t } = useTranslation();

    /**
     * - If item contains Alliance and group of GUs, set Alliance as parent value and list of GUs as children.
     * - If item is isolated GU, set it to parent with no children.
     */
    const filtersTree = options.map(item => {
        if (item.gu.find(gu => gu === item.name)) {
            return {
                title: item.name,
                key: item.name,
                value: item.name,
            };
        }
        return {
            title: item.name,
            key: item.name,
            value: item.name,
            children: item.gu.map(itemGu => {
                return {
                    title: itemGu,
                    key: itemGu,
                    value: itemGu,
                }
            })
        }
		
    })

    useEffect(() => {
        /**
		 * - If `clear` is `true`, set filter value to none
		 * - If not, change value according to user triggered action
		 */
        if (clear) {
            onChange([]);
            setValue([]);
        } else {
            onChange(value);
        }
    }, [value, clear, onChange]);

    return (
        <TreeSelect
            placeholder={t('selectFilter')}
            optionLabelProp='label'
            allowClear='true'
            showSearch='true'
            treeDefaultExpandAll='false'
            treeCheckable='true'
            virtual={false}
            value={clear ? [] : value}
            showCheckedStrategy='SHOW_PARENT'
            onChange={setValue}
            treeData={filtersTree}
            {...props}
        >
        </TreeSelect>
    );
}

CITTreeSelect.defaultProps = {
    options: [],
    onChange: noop,
    clear: false,
};

export default CITTreeSelect;
